import _define_property from "@swc/helpers/src/_define_property.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Combobox, Group, InputBase, Tooltip, useCombobox, useTableContext } from "@components/mantine";
import { useMemo } from "react";
import { Locales } from "../../strings/Locales.js";
import { PrivilegesTableUtils } from "./PrivilegesTableUtils.js";
import { PrivilegesTableWarnings } from "./PrivilegesTableWarnings.js";
import { usePrivilegeRow } from "./PrivilegeTableContext.js";
var forbidden = {
    value: "NONE",
    label: Locales.format("AccessLevelPicker.option.NONE"),
    toPrivileges: function() {
        return [];
    },
    fromPrivileges: function() {
        return true;
    }
};
var editViewNone = [
    {
        value: "EDIT",
        label: Locales.format("AccessLevelPicker.option.EDIT"),
        toPrivileges: function(owner, targetDomain, level, systemPrivileges) {
            var createPrivilegesExists = systemPrivileges.some(PrivilegesTableUtils.isCreatePrivilege);
            var viewPrivilegesExists = systemPrivileges.some(function(priv) {
                return priv.type === "VIEW";
            });
            var privileges = [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "EDIT",
                    targetId: "*"
                }
            ];
            if (createPrivilegesExists) {
                privileges.push({
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "CREATE",
                    targetId: "*"
                });
            }
            if (viewPrivilegesExists) {
                privileges.push({
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                });
            }
            return privileges;
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "EDIT";
            });
        }
    },
    {
        value: "VIEW",
        label: Locales.format("AccessLevelPicker.option.VIEW"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "VIEW";
            });
        }
    },
    forbidden
];
var canCannot = [
    {
        value: "ALLOWED",
        label: Locales.format("AccessLevelPicker.option.ALLOWED"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var owner = param.owner, targetDomain = param.targetDomain, targetId = param.targetId;
                return owner && targetDomain && targetId === "*";
            });
        }
    },
    forbidden
];
var createOrNone = [
    {
        value: "CREATE",
        label: Locales.format("AccessLevelPicker.option.CREATE"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "CREATE",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "CREATE";
            });
        }
    },
    forbidden
];
var pushAndView = [
    {
        value: "PUSH_AND_VIEW",
        label: Locales.format("AccessLevelPicker.option.PUSH_AND_VIEW"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "EDIT",
                    targetId: "*"
                },
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "EDIT";
            }) && privileges.some(function(param) {
                var type = param.type;
                return type === "VIEW";
            });
        }
    },
    {
        value: "VIEW",
        label: Locales.format("AccessLevelPicker.option.VIEW"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "VIEW";
            });
        }
    },
    {
        value: "PUSH",
        label: Locales.format("AccessLevelPicker.option.PUSH"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "EDIT",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "EDIT";
            });
        }
    },
    forbidden
];
var granularEdit = [
    {
        value: "EDIT_ALL",
        label: Locales.format("AccessLevelPicker.option.EDIT_ALL"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "EDIT",
                    targetId: "*"
                },
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "CREATE",
                    targetId: "*"
                },
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type, targetId = param.targetId;
                return type === "EDIT" && targetId === "*";
            });
        }
    },
    {
        value: "CUSTOM",
        label: Locales.format("AccessLevelPicker.option.CUSTOM"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "EDIT";
            });
        }
    },
    {
        value: "VIEW_ALL",
        label: Locales.format("AccessLevelPicker.option.VIEW_ALL"),
        toPrivileges: function(owner, targetDomain, level) {
            return [
                {
                    owner: owner,
                    targetDomain: targetDomain,
                    level: level,
                    type: "VIEW",
                    targetId: "*"
                }
            ];
        },
        fromPrivileges: function(privileges) {
            return privileges.some(function(param) {
                var type = param.type;
                return type === "VIEW";
            });
        }
    },
    forbidden
];
var granularAllow = function(allowString) {
    return [
        {
            value: allowString,
            label: Locales.format("AccessLevelPicker.option.".concat(allowString)),
            toPrivileges: function(owner, targetDomain, level) {
                return [
                    {
                        owner: owner,
                        targetDomain: targetDomain,
                        level: level,
                        targetId: "*"
                    }
                ];
            },
            fromPrivileges: function(privileges) {
                return privileges.some(function(param) {
                    var targetId = param.targetId;
                    return targetId === "*";
                });
            }
        },
        {
            value: "CUSTOM",
            label: Locales.format("AccessLevelPicker.option.CUSTOM"),
            toPrivileges: function() {
                return [];
            },
            fromPrivileges: function(privileges) {
                return privileges.some(function(param) {
                    var targetId = param.targetId;
                    return !!targetId;
                });
            }
        },
        forbidden
    ];
};
var getBaseOptionsForPrivilegeId = function(privilegeId) {
    if (PrivilegesTableUtils.isGranularPrivilegeId(privilegeId)) {
        switch(privilegeId){
            case "PUSH_IDENTITY_PLATFORM":
                return granularAllow("ALLOW_ALL_PROVIDERS");
            case "PUSH_DOCUMENT_PLATFORM":
                return granularAllow("ALLOW_FOR_ALL_SOURCES");
            default:
                return granularEdit;
        }
    }
    switch(privilegeId){
        case "ADMINISTRATE_USAGE_ANALYTICS":
        case "IMPERSONATE_USAGE_ANALYTICS":
        case "QUERY_SUGGEST_USAGE_ANALYTICS":
        case "VIEW_ALL_REPORTS_USAGE_ANALYTICS":
        case "DELETE_USER_ANALYTICS_DATA_USAGE_ANALYTICS":
        case "IMPERSONATE_SEARCH_API":
        case "EXECUTE_QUERY_SEARCH_API":
        case "VIEW_ALL_CONTENT_SEARCH_API":
        case "AUTHENTICATION_EDITOR_SEARCH_API":
        case "USE_CASE_ASSIST_CUSTOMER_SERVICE":
        case "ALLOW_CONTENT_PREVIEW_COVEO_ML":
        case "MANAGE_CUSTOMER_LICENSE_PLATFORM_INTERNAL":
        case "MANAGE_CUSTOMER_LICENSE_OVERRIDES_PLATFORM_INTERNAL":
        case "KNOWLEDGE_HUB_KNOWLEDGE":
        case "CHUNK_INSPECTOR_KNOWLEDGE":
        case "EXECUTE_AGENT_QUERY_KNOWLEDGE":
        case "EXECUTE_AGENT_QUERY_COVEO_ML":
            return canCannot;
        case "ANALYTICS_DATA_USAGE_ANALYTICS":
            return pushAndView;
        case "ORGANIZATION_INFRASTRUCTURE_PLATFORM_GLOBAL":
            return createOrNone;
        default:
            return editViewNone;
    }
};
var getOptions = function(privilegeId, availablePrivileges, systemPrivileges) {
    return getBaseOptionsForPrivilegeId(privilegeId).filter(function(option) {
        return option.fromPrivileges(systemPrivileges);
    }).map(function(option) {
        return _object_spread_props(_object_spread({}, option), {
            disabled: !option.fromPrivileges(availablePrivileges)
        });
    });
};
export var AccessLevelPicker = function(row) {
    var ref = usePrivilegeRow(row.id), onChange = ref.onChange, selectedPrivileges = ref.selectedPrivileges, customSelected = ref.customSelected, selectCustom = ref.selectCustom, readOnly = ref.readOnly;
    var ref1 = useTableContext(), table = ref1.store;
    var combobox = useCombobox({
        onDropdownClose: function() {
            return combobox.resetSelectedOption();
        }
    });
    var data = useMemo(function() {
        return getOptions(row.id, row.availablePrivileges, row.systemPrivileges);
    }, [
        row.id,
        row.availablePrivileges,
        row.systemPrivileges
    ]);
    var selectedOption = data.find(function(option) {
        return customSelected ? option.value === "CUSTOM" : option.fromPrivileges(selectedPrivileges);
    });
    var options = data.map(function(option) {
        return /*#__PURE__*/ _jsx(Tooltip, {
            label: option.value === "CUSTOM" ? Locales.format("AccessLevelPicker.disabledCustom", {
                name: row.name,
                resourceName: Locales.formatOrHumanize("PrivilegesRows.".concat(row.targetDomain, ".granular"), {
                    defaultTranslation: row.name
                })
            }) : Locales.format("AccessLevelPicker.disabledOption", {
                accessLevel: option.label,
                name: row.name
            }),
            disabled: !option.disabled,
            children: /*#__PURE__*/ _jsx(Combobox.Option, {
                value: option.value,
                disabled: option.disabled,
                children: option.value === "NONE" ? Locales.format("AccessLevelPicker.option.noAccess") : option.label
            })
        }, option.value);
    });
    return /*#__PURE__*/ _jsxs(Group, {
        wrap: "nowrap",
        gap: "xs",
        children: [
            /*#__PURE__*/ _jsxs(Combobox, {
                store: combobox,
                readOnly: readOnly,
                onOptionSubmit: function(value) {
                    onChange(data.find(function(option) {
                        return option.value === value;
                    }).toPrivileges(row.owner, row.targetDomain, row.level, row.systemPrivileges).map(PrivilegesTableUtils.removeUndefinedProperties));
                    if (value === "CUSTOM") {
                        selectCustom(true);
                        table.setExpanded(_define_property({}, row.id, true));
                    } else {
                        selectCustom(false);
                    }
                    combobox.closeDropdown();
                },
                children: [
                    /*#__PURE__*/ _jsx(Combobox.Target, {
                        children: /*#__PURE__*/ _jsx(InputBase, {
                            pointer: true,
                            rightSection: /*#__PURE__*/ _jsx(Combobox.Chevron, {}),
                            rightSectionPointerEvents: "none",
                            w: 178,
                            readOnly: readOnly,
                            onClick: function() {
                                if (!readOnly) {
                                    combobox.toggleDropdown();
                                }
                            },
                            value: selectedOption.label,
                            onChange: function() {
                                combobox.openDropdown();
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx(Combobox.Dropdown, {
                        children: /*#__PURE__*/ _jsx(Combobox.Options, {
                            children: options
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(PrivilegesTableWarnings, _object_spread({}, row))
        ]
    });
};

//# sourceMappingURL=AccessLevelPicker.js.map